/**
 * print-utils.ts - Shared Print Utilities
 *
 * Common utilities for printing tournament pairings and standings,
 * including shared header generation and styling.
 */

import type { Tournament } from '../../types/api';
import { escapeHtml } from '../utils/security';
import { t } from '../utils/i18n-helper';
import { simplifyVenueName } from '../utils/formatting';

/**
 * Print options for controlling what to include
 */
export interface PrintOptions {
  includeDescription: boolean;
  includeMeta: boolean;
}

/**
 * Generate common print header CSS styles
 */
export function getPrintHeaderStyles(): string {
  return `
    * { margin: 0; padding: 0; box-sizing: border-box; }

    @page {
      size: A4;
      margin: 15mm 15mm;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
      color: #1a1a1a;
      line-height: 1.6;
      background: white;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
    }

    /* Header Section */
    .print-header {
      display: flex;
      align-items: flex-start;
      gap: 20px;
      padding-bottom: 12px;
      margin-bottom: 16px;
    }

    .tournament-logo {
      width: 70px;
      height: 70px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .header-content {
      flex: 1;
      min-width: 0;
    }

    .tournament-title {
      font-size: 24px;
      font-weight: 600;
      color: #0a0a0a;
      margin-bottom: 4px;
      letter-spacing: -0.3px;
      line-height: 1.2;
    }

    .tournament-subtitle {
      font-size: 13px;
      color: #5a5a5a;
      margin-bottom: 8px;
      line-height: 1.4;
    }

    /* Rich text content styles */
    .tournament-subtitle p {
      margin: 0 0 4px 0;
    }

    .tournament-subtitle p:last-child {
      margin-bottom: 0;
    }

    .tournament-subtitle strong {
      font-weight: 600;
      color: #3a3a3a;
    }

    .tournament-subtitle em {
      font-style: italic;
    }

    .tournament-subtitle u {
      text-decoration: underline;
    }

    .tournament-subtitle s {
      text-decoration: line-through;
    }

    .tournament-subtitle h1,
    .tournament-subtitle h2,
    .tournament-subtitle h3 {
      font-weight: 600;
      color: #3a3a3a;
      margin: 4px 0 2px 0;
      line-height: 1.3;
    }

    .tournament-subtitle h1 {
      font-size: 15px;
    }

    .tournament-subtitle h2 {
      font-size: 14px;
    }

    .tournament-subtitle h3 {
      font-size: 13px;
    }

    .tournament-subtitle ul,
    .tournament-subtitle ol {
      margin: 4px 0;
      padding-left: 20px;
    }

    .tournament-subtitle li {
      margin: 2px 0;
    }

    .tournament-subtitle a {
      color: #0066cc;
      text-decoration: underline;
    }

    .tournament-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 4px 20px;
      font-size: 11px;
      color: #6a6a6a;
    }

    .meta-item {
      display: flex;
      align-items: baseline;
      white-space: nowrap;
    }

    .meta-label {
      font-weight: 500;
      margin-right: 5px;
      color: #3a3a3a;
      text-transform: uppercase;
      font-size: 10px;
      letter-spacing: 0.4px;
    }

    .meta-value {
      color: #1a1a1a;
      font-weight: 400;
    }

    /* Document Title */
    .document-title {
      font-size: 18px;
      font-weight: 600;
      color: #0a0a0a;
      margin: 0 0 12px 0;
      padding-bottom: 8px;
      letter-spacing: -0.2px;
    }

    /* Print Footer */
    .print-footer {
      margin-top: 48px;
      padding-top: 16px;
      font-size: 10px;
      color: #9a9a9a;
      text-align: center;
      letter-spacing: 0.3px;
    }
  `;
}

/**
 * Generate common table styles for print
 */
export function getPrintTableStyles(): string {
  return `
    /* Tables */
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 32px;
    }

    thead {
      border-bottom: 1px solid #e0e0e0;
    }

    th {
      padding: 8px 10px;
      text-align: left;
      color: #666;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 9px;
      letter-spacing: 0.8px;
    }

    tbody tr {
      border-bottom: 1px solid #ebebeb;
      page-break-inside: avoid;
      page-break-after: auto;
    }

    tbody tr:last-child {
      border-bottom: none;
    }

    /* Print optimizations */
    @media print {
      body {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
      }

      thead {
        display: table-header-group;
      }

      tbody tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
    }
  `;
}

/**
 * Convert tournament logo to absolute URL
 */
export function getAbsoluteLogoUrl(tournament: Tournament): string {
  const logoToUse = tournament.logo || '/images/tournament-logo.png';

  if (logoToUse.startsWith('data:')) {
    return logoToUse;
  } else if (logoToUse.startsWith('http://') || logoToUse.startsWith('https://')) {
    return logoToUse;
  } else {
    return window.location.origin + logoToUse;
  }
}

/**
 * Get current locale from i18n system
 * Maps user's selected language to appropriate locale for date formatting
 */
function getLocale(): string {
  if (window.i18n?.getLocale) {
    return window.i18n.getLocale();
  }

  // Fallback: check localStorage for user's selected language
  const savedLang = localStorage.getItem('language');
  if (savedLang) {
    const localeMap: Record<string, string> = {
      'ar': 'ar-SA', 'bg': 'bg-BG', 'bn': 'bn-BD', 'cs': 'cs-CZ',
      'da': 'da-DK', 'de': 'de-DE', 'el': 'el-GR', 'en': 'en-US',
      'es': 'es-ES', 'fi': 'fi-FI', 'fr': 'fr-FR', 'hi': 'hi-IN',
      'hu': 'hu-HU', 'id': 'id-ID', 'it': 'it-IT', 'ja': 'ja-JP',
      'ko': 'ko-KR', 'no': 'no-NO', 'pl': 'pl-PL', 'pt': 'pt-PT',
      'ro': 'ro-RO', 'ru': 'ru-RU', 'sr': 'sr-RS', 'sv': 'sv-SE',
      'tr': 'tr-TR', 'uk': 'uk-UA', 'zh': 'zh-CN'
    };
    return localeMap[savedLang] || savedLang;
  }

  // Last resort: use browser locale
  return navigator.language || 'en-US';
}

/**
 * Format date for display (long format)
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const locale = getLocale();
  return date.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Format date with weekday for round schedule (e.g., "Saturday, 1.11.2025")
 */
export function formatDateWithWeekday(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  const locale = getLocale();
  return date.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  });
}

/**
 * Format tournament type for display
 */
export function formatTournamentType(format: string): string {
  return format.charAt(0).toUpperCase() + format.slice(1).replace(/-/g, ' ');
}

/**
 * Generate tournament header HTML
 */
export function generateTournamentHeader(tournament: Tournament, options: PrintOptions = { includeDescription: true, includeMeta: true }): string {
  const tournamentTitle = tournament.name;
  const description = tournament.description || '';
  const logoUrl = getAbsoluteLogoUrl(tournament);
  const formatType = formatTournamentType(tournament.format);

  return `
    <div class="print-header">
      ${logoUrl ? `<img src="${logoUrl}" alt="Tournament Logo" class="tournament-logo" onerror="this.style.display='none'">` : ''}
      <div class="header-content">
        <h1 class="tournament-title">${escapeHtml(tournamentTitle)}</h1>
        ${options.includeDescription && description ? `<div class="tournament-subtitle">${description}</div>` : ''}
        ${options.includeMeta ? `<div class="tournament-meta">
          ${tournament.venue ? `
            <div class="meta-item">
              <span class="meta-label">${t('pairings.printVenue', 'Venue:')}</span>
              <span class="meta-value">${escapeHtml(simplifyVenueName(tournament.venue))}</span>
            </div>
          ` : ''}
          <div class="meta-item">
            <span class="meta-label">${t('pairings.printFormat', 'Format:')}</span>
            <span class="meta-value">${formatType}</span>
          </div>
          ${tournament.gameCadence ? `
            <div class="meta-item">
              <span class="meta-label">${t('pairings.printTimeControl', 'Time Control:')}</span>
              <span class="meta-value">${escapeHtml(tournament.gameCadence)}</span>
            </div>
          ` : ''}
          ${tournament.startDate ? `
            <div class="meta-item">
              <span class="meta-label">${t('pairings.printTournamentDates', 'Tournament Dates:')}</span>
              <span class="meta-value">${formatDate(tournament.startDate)}${tournament.endDate ? ' - ' + formatDate(tournament.endDate) : ''}</span>
            </div>
          ` : ''}
          ${tournament.numberOfRounds ? `
            <div class="meta-item">
              <span class="meta-label">${t('pairings.printTotalRounds', 'Total Rounds:')}</span>
              <span class="meta-value">${tournament.numberOfRounds}</span>
            </div>
          ` : ''}
        </div>` : ''}
      </div>
    </div>
  `;
}

/**
 * Show print options dialog and return user's choices
 */
export function showPrintOptionsDialog(): Promise<PrintOptions | null> {
  return new Promise((resolve) => {
    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

    // Create modal dialog
    const dialog = document.createElement('div');
    dialog.style.cssText = `
      background: white;
      border-radius: 8px;
      padding: 24px;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    `;

    dialog.innerHTML = `
      <h3 style="margin: 0 0 16px 0; font-size: 18px; font-weight: 600; color: #1a1a1a;">
        ${t('print.options', 'Print Options')}
      </h3>
      <div style="margin-bottom: 20px;">
        <label class="toggle-switch-container" style="margin-bottom: 12px;">
          <input type="checkbox" class="toggle-switch-input" id="print-include-description" checked>
          <span class="toggle-switch-slider"></span>
          <span class="toggle-switch-label">${t('print.includeDescription', 'Include tournament description')}</span>
        </label>
        <label class="toggle-switch-container">
          <input type="checkbox" class="toggle-switch-input" id="print-include-meta" checked>
          <span class="toggle-switch-slider"></span>
          <span class="toggle-switch-label">${t('print.includeInfo', 'Include tournament info (venue, format, dates)')}</span>
        </label>
      </div>
      <div style="display: flex; gap: 10px; justify-content: flex-end;">
        <button id="print-cancel" style="padding: 8px 16px; border: 1px solid #ddd; background: white; border-radius: 4px; cursor: pointer; font-size: 14px;">
          ${t('common.cancel', 'Cancel')}
        </button>
        <button id="print-confirm" style="padding: 8px 16px; border: none; background: #007bff; color: white; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500;">
          ${t('common.print', 'Print')}
        </button>
      </div>
    `;

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    const descriptionCheckbox = dialog.querySelector('#print-include-description') as HTMLInputElement;
    const metaCheckbox = dialog.querySelector('#print-include-meta') as HTMLInputElement;
    const cancelBtn = dialog.querySelector('#print-cancel') as HTMLButtonElement;
    const confirmBtn = dialog.querySelector('#print-confirm') as HTMLButtonElement;

    const cleanup = () => {
      document.body.removeChild(overlay);
    };

    cancelBtn.addEventListener('click', () => {
      cleanup();
      resolve(null);
    });

    confirmBtn.addEventListener('click', () => {
      const options: PrintOptions = {
        includeDescription: descriptionCheckbox.checked,
        includeMeta: metaCheckbox.checked
      };
      cleanup();
      resolve(options);
    });

    // Close on overlay click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        cleanup();
        resolve(null);
      }
    });

    // Close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanup();
        resolve(null);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  });
}

/**
 * Generate print footer HTML
 * NOTE: Tier system suspended - always show simple "nextround" branding without tier mentions
 */
export function generatePrintFooter(tournament: Tournament): string {
  const locale = getLocale();
  // SUSPENDED: Tier system hidden until app reaches popularity threshold
  // const subscriptionTier = tournament.subscriptionTier || 'free';
  // const planName = subscriptionTier === 'pro' ? 'Pro' : 'Free';
  // const limitText = subscriptionTier === 'free' ? ` (${t('pairings.printFreeLimits', 'Limited to 20 players and non-rated tournaments')})` : '';

  return `
    <div class="print-footer">
      ${t('pairings.printGeneratedOnWith', 'Generated on')} ${new Date().toLocaleString(locale, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })} ${t('pairings.printWith', 'with')} <strong>nextround</strong>
    </div>
  `;
}

// Expose print utilities to window for use in share/embed pages
declare global {
  interface Window {
    printUtils: {
      getPrintHeaderStyles: typeof getPrintHeaderStyles;
      getPrintTableStyles: typeof getPrintTableStyles;
      generateTournamentHeader: typeof generateTournamentHeader;
      generatePrintFooter: typeof generatePrintFooter;
      getAbsoluteLogoUrl: typeof getAbsoluteLogoUrl;
      formatDate: typeof formatDate;
    };
  }
}

window.printUtils = {
  getPrintHeaderStyles,
  getPrintTableStyles,
  generateTournamentHeader,
  generatePrintFooter,
  getAbsoluteLogoUrl,
  formatDate,
  showPrintOptionsDialog
};
